import { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { GripVertical, Pencil, Trash2, Plus, ChevronDown, ChevronRight } from 'lucide-react'
import { CampoItem } from './CampoItem'
import type { PasoFormulario, CampoFormulario } from '../../types'

interface PasoCardProps {
  paso: PasoFormulario
  onEditPaso: () => void
  onDeletePaso: () => void
  onAddCampo: () => void
  onEditCampo: (campo: CampoFormulario) => void
  onDeleteCampo: (campoId: number) => void
  onReorderCampos: (orden: number[]) => void
}

export function PasoCard({
  paso,
  onEditPaso,
  onDeletePaso,
  onAddCampo,
  onEditCampo,
  onDeleteCampo,
  onReorderCampos,
}: PasoCardProps) {
  const [expanded, setExpanded] = useState(true)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: paso.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleCampoDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = paso.campos.findIndex((c) => c.id === active.id)
    const newIndex = paso.campos.findIndex((c) => c.id === over.id)
    const newOrder = arrayMove(paso.campos, oldIndex, newIndex)
    onReorderCampos(newOrder.map((c) => c.id))
  }

  return (
    <Card ref={setNodeRef} style={style} className="border border-gray-300 dark:border-gray-700">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center gap-2">
          <button {...attributes} {...listeners} className="cursor-grab text-muted-foreground hover:text-foreground">
            <GripVertical className="h-5 w-5" />
          </button>

          <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>

          <div className="flex-1 min-w-0">
            <span className="font-semibold">Paso {paso.numero_paso}:</span>{' '}
            <span className="text-sm">{paso.titulo}</span>
            {paso.descripcion && (
              <p className="text-xs text-muted-foreground truncate">{paso.descripcion}</p>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" className="h-8" onClick={onAddCampo}>
              <Plus className="h-3 w-3 mr-1" /> Campo
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onEditPaso}>
              <Pencil className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={onDeletePaso}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 pb-3 px-4">
          {paso.campos.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCampoDragEnd}>
              <SortableContext items={paso.campos.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-1 ml-7">
                  {paso.campos.map((campo) => (
                    <CampoItem
                      key={campo.id}
                      campo={campo}
                      onEdit={() => onEditCampo(campo)}
                      onDelete={() => onDeleteCampo(campo.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <p className="text-sm text-muted-foreground ml-7">
              Sin campos. Haz click en "+ Campo" para agregar.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  )
}
